import { useQuery } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { FaFilter } from 'react-icons/fa'
import { FaFilterCircleXmark, FaUserPlus } from 'react-icons/fa6'
import { useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import { Breadcrumb } from '../../../components/Breadcumb'
import { Button } from '../../../components/Button'
import { Pagination } from '../../../components/Pagination'
import { usePermission } from '../../../hooks/usePermission'
import { getUsers, toggleRemoveUser, User } from '../../../services/userServices'
import { FilterUsersFormModal } from './components/FilterUsersFormModal'
import { ModalUpsertUser } from './components/ModalUpsertUser'
import { UsersTable } from './components/Table/UsersTable'

export function Users() {
  const [isUpsertUserModal, setIsUpsertUserModal] = useState(false)
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false)
  const [onlyRemovedUsers, setOnlyRemovedUsers] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const { upsertUser, isObserver } = usePermission()

  const [searchParams, setSearchParams] = useSearchParams()

  const id = searchParams.get('id') || undefined
  const name = searchParams.get('name') || undefined
  const role = searchParams.get('role') || undefined

  const page = z.coerce
    .number()
    .parse(searchParams.get('page') ?? '1')

  function toggleFiltersModalOpen() {
    setIsFiltersModalOpen(open => !open)
  }

  function toggleUpsertUserModal() {
    setIsUpsertUserModal(open => !open)
    setSelectedUser(null)
  }

  function handleClearFilters() {
    setSearchParams(state => {
      state.delete('id')
      state.delete('name')
      state.delete('role')
      state.set('page', '1')
      setOnlyRemovedUsers(false)
      return state
    })
  }

  function handleSetOnlyRemovedUsers() {
    setOnlyRemovedUsers(true)
  }

  const handleEditUser = useCallback((user: User) => {
    setSelectedUser(user)
    setIsUpsertUserModal(true)
  }, [])

  const { data: result, refetch } = useQuery({
    queryKey: ['users', page, id, name, role, onlyRemovedUsers],
    queryFn: () => getUsers({
      id,
      name,
      role,
      is_removed: onlyRemovedUsers,
      page,
    }),
  })

  const handleRefetchList = useCallback(() => {
    refetch()
  }, [refetch])

  function hasFilters() {
    if (id || name || role || onlyRemovedUsers) {
      return true
    }
    return false
  }

  function handlePaginate(page: number) {
    setSearchParams(state => {
      state.set('page', (page).toString())

      return state
    })
  }

  const handleToggleRemoveUser = useCallback(async (id: string, isRemoved: boolean, name: string) => {
    const confirmation = confirm(`Tem certeza que deseja ${isRemoved
      ? 'restaurar'
      : 'remover'} o usuário ${name}`)
    if (!confirmation) return
    const result = await toggleRemoveUser({ id, isRemoved })

    if (result) {
      handleRefetchList()
    }
  }, [handleRefetchList])

  return (
    <div className="min-h-screen">
      <div className="flex flex-col mx-auto max-w-screen-lg">
        <div className="px-2 lg:px-0 my-4">
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/dashboard' },
              { label: 'Usuários', path: '/users' },
            ]}
          />
        </div>
        <header className="flex flex-col sm:flex-row justify-between items-center m-4 lg:mx-0">
          <h1
            className="mb-4 sm:mb-0 font-bold text-2xl"
          >
            Usuários
          </h1>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={handleClearFilters}
              size="icon"
              disabled={!hasFilters()}
              className="disabled:border-slate-400 disabled:text-slate-400 disabled:hover:border-slate-400 disabled:bg-transparent disabled:cursor-default"
              title="Limpar filtros"
            >
              <FaFilterCircleXmark />
            </Button>
            <Button
              variant="outline"
              onClick={toggleFiltersModalOpen}
              size="icon"
            >
              <FaFilter />
            </Button>
            <div className="sm:pl-6">
              <Button
                variant="outline"
                onClick={toggleUpsertUserModal}
                disabled={!upsertUser || isObserver}
                className="disabled:border-slate-400 disabled:text-slate-400 disabled:hover:border-slate-400 disabled:bg-transparent disabled:cursor-default"
                title="Adicionar usuário"
              >
                Criar usuário
                <FaUserPlus className="ml-4" />
              </Button>
            </div>
          </div>
        </header>
        <main className="px-2 lg:px-0 flex-1 h-full">
          {result &&
            <UsersTable
              users={result.users}
              onRemoveUser={handleToggleRemoveUser}
              onEdit={handleEditUser}
            />}
        </main>
        <footer className="mx-4 lg:mx-0 mt-2">
          {result &&
            <Pagination page={page} totalCount={result.totalCount} pageQty={result.pageQty} onPageChange={handlePaginate} />}
        </footer>
      </div>
      {isFiltersModalOpen &&
        <FilterUsersFormModal onClose={toggleFiltersModalOpen} onSetOnlyRemovedUsers={handleSetOnlyRemovedUsers} />}
      {isUpsertUserModal &&
        <ModalUpsertUser onClose={toggleUpsertUserModal} refetchList={handleRefetchList} user={selectedUser} />}
    </div>
  )
}
